import { MouseEventHandler, useMemo } from 'react';
import { Button, EXTRA_SMALL, TERTIARY } from '@glu/buttons-react';
import { ContainerArrowDownAshIcon } from '@glu/icons-react';
import locale from '@glu/locale';
import Popover from '@glu/popover-react';
import useStyles from './Export.styles';

/**
 * Data for button displayed in export menu.
 */
interface IMenuItem {
  /**
   * Text shown in the UI in the popover for the menuItem.
   */
  label: string;
  /**
   * ClickHandler for button that wraps the export icon.
   */
  onClick: MouseEventHandler<HTMLButtonElement>;
}

/**
 * Props for the export component. If menuItems are passed as a prop then
 * onClick will NOT work. Only expected to pass one prop or the other.
 */
export interface IExportProps {
  /**
   * When defined will become the clickHandler for the export icon.
   */
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * When defined will render a list of buttons in a popover that is
   * opened by clicking on the export icon. Will disable onClick.
   */
  menuItems?: IMenuItem[];
}

const popperOptions = {
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, -5],
      },
    },
  ],
};

/**
 * Export icon displayed in the controlbar for the ComposedViewAgGrid.
 * Wraps the exportContent in the conditional popover so that the export icon is
 * only wrapped by the popover if there are menuItems. Also disables onClick if
 * menuItems are passed as a prop.
 */
const Export = ({ menuItems, onClick }: IExportProps) => {
  const classes = useStyles();
  const exportIcon = useMemo(
    () => (
      <ContainerArrowDownAshIcon
        actionable={{
          'aria-label': locale.get('dataComponents.export'),
          title: locale.get('dataComponents.export'),
        }}
        data-qa="exportButton"
        onClick={onClick}
        title={locale.get('dataComponents.export')}
      />
    ),
    [onClick]
  );
  if (menuItems) {
    return (
      <Popover
        className={classes.popover}
        content={({ closePopover }) =>
          menuItems.map((menuItem) => (
            <Button
              key={menuItem.label}
              className={classes.menuButton}
              data-qa={menuItem.label}
              onClick={(e) => {
                menuItem.onClick(e);
                closePopover();
              }}
              size={EXTRA_SMALL}
              variant={TERTIARY}
            >
              {menuItem.label}
            </Button>
          ))
        }
        enablePopoverClick
        noArrow
        placement="bottom-end"
        popperOptions={popperOptions}
      >
        <div>{exportIcon}</div>
      </Popover>
    );
  }
  return exportIcon;
};

export default Export;
